import { Navigation, MapPin, PackageCheck, CheckCircle2, Clock, Wifi, WifiOff, Truck } from "lucide-react";

const PHASE_ICON = {
  heading_to_pickup: MapPin,
  heading_to_delivery: Truck,
  delivered: CheckCircle2,
  cancelled: PackageCheck,
  waiting_driver: Clock,
};

const PHASE_TONE = {
  heading_to_pickup: "from-amber-500 to-orange-500",
  heading_to_delivery: "from-dapnow-blue to-blue-600",
  delivered: "from-emerald-500 to-emerald-600",
  cancelled: "from-zinc-500 to-zinc-700",
  waiting_driver: "from-zinc-400 to-zinc-500",
};

function relativeTime(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const sec = Math.floor(diff / 1000);
  if (sec < 10) return "pochi secondi fa";
  if (sec < 60) return `${sec} sec fa`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min fa`;
  const h = Math.floor(min / 60);
  return `${h} h fa`;
}

function formatEta(min) {
  if (min === null || min === undefined) return null;
  const m = Math.max(0, Math.round(min));
  if (m < 1) return "meno di 1 min";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h} h` : `${h} h ${r} min`;
}

function formatKm(km) {
  if (km === null || km === undefined) return null;
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}

/**
 * TrackingETACard
 * Shows current phase + ETA + distance + last update + live indicator.
 * Falls back gracefully when tracking is not active or Distance Matrix is not available.
 */
export default function TrackingETACard({ tracking, status }) {
  if (!tracking) return null;

  const phase = tracking.phase || status || "waiting_driver";
  const phaseLabel = tracking.phase_label || "Aggiornamento in corso…";
  const isActive = !!tracking.tracking_active;
  const eta = tracking?.driver?.eta_min ?? tracking.eta_min;
  const distance = tracking?.driver?.distance_to_target_km ?? tracking.distance_to_target_km;
  const lastUpdate = tracking?.driver?.last_location_at;
  const driverName = tracking?.driver?.name;
  const PhaseIcon = PHASE_ICON[phase] || Navigation;
  const tone = PHASE_TONE[phase] || PHASE_TONE.heading_to_delivery;
  const target = tracking.eta_target;

  const etaStr = formatEta(eta);
  const distStr = formatKm(distance);

  // Compose the headline
  let headline = phaseLabel;
  if (etaStr && distStr) {
    headline = `${phaseLabel} · ${etaStr} · ${distStr}`;
  } else if (etaStr) {
    headline = `${phaseLabel} · arrivo stimato ${etaStr}`;
  }

  return (
    <div
      className={`relative overflow-hidden bg-gradient-to-br ${tone} text-white p-4 sm:p-5`}
      data-testid="tracking-eta-card"
    >
      {/* live pulse */}
      <div className="absolute top-3 right-3 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
        {isActive ? (
          <>
            <span className="relative flex h-2 w-2" data-testid="tracking-live-pulse">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-white"></span>
            </span>
            <Wifi className="size-3" /> Tracking live
          </>
        ) : (
          <>
            <WifiOff className="size-3" /> {phase === "delivered" ? "Consegnato" : phase === "cancelled" ? "Annullato" : "Standby"}
          </>
        )}
      </div>

      <div className="flex items-start gap-3">
        <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-2xl bg-white/15 backdrop-blur ring-1 ring-white/20">
          <PhaseIcon className="size-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-bold text-white/80">
            {target === "pickup" ? "Verso il punto di ritiro" : target === "delivery" ? "Verso il punto di consegna" : "Stato"}
          </p>
          <h3 className="text-lg sm:text-xl font-display font-bold tracking-tight leading-tight mt-0.5" data-testid="tracking-headline">
            {headline}
          </h3>
          {driverName && isActive && (
            <p className="text-xs text-white/85 mt-1">Trasportatore: <strong>{driverName}</strong></p>
          )}
        </div>
      </div>

      {(etaStr || distStr) && (
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-white/15 backdrop-blur px-3 py-2.5 ring-1 ring-white/15">
            <p className="text-[9px] uppercase tracking-wider font-bold text-white/70">ETA</p>
            <p className="text-base sm:text-lg font-display font-extrabold tabular-nums mt-0.5" data-testid="tracking-eta-value">
              {etaStr || "—"}
            </p>
          </div>
          <div className="rounded-xl bg-white/15 backdrop-blur px-3 py-2.5 ring-1 ring-white/15">
            <p className="text-[9px] uppercase tracking-wider font-bold text-white/70">Distanza</p>
            <p className="text-base sm:text-lg font-display font-extrabold tabular-nums mt-0.5" data-testid="tracking-distance-value">
              {distStr || "—"}
            </p>
          </div>
          <div className="rounded-xl bg-white/15 backdrop-blur px-3 py-2.5 ring-1 ring-white/15">
            <p className="text-[9px] uppercase tracking-wider font-bold text-white/70">Aggiornato</p>
            <p className="text-[11px] sm:text-xs font-bold mt-1" data-testid="tracking-last-update">
              {relativeTime(lastUpdate) || "in attesa…"}
            </p>
          </div>
        </div>
      )}

      {!etaStr && isActive && (
        <p className="mt-3 text-xs text-white/85 italic" data-testid="tracking-eta-fallback">
          Calcolo della stima in corso… {tracking.matrix_provider === "haversine" ? "(routing via fallback)" : ""}
        </p>
      )}
    </div>
  );
}
